import { cloneElement, createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type ApiError } from "./api";
import { Icon, type IconName } from "./icons";

/* ---------- Session ---------- */

export type Me = { user: null | { id: string; displayName: string; avatarUrl?: string; roleIds?: string[] }; permissions: string[]; expiresAt?: string };
export const SessionContext = createContext<Me>({ user: null, permissions: [] });
export const useSession = () => useContext(SessionContext);

/* ---------- Data ---------- */

export type AsyncState<T> = { loading: boolean; data?: T; error?: ApiError; reload: () => void };

/** Fetches `url`. Data is kept while the same URL refreshes (no flashing), but never leaks across URLs. */
export function useApi<T>(url: string, key: unknown = ""): AsyncState<T> {
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState<{ url: string; loading: boolean; data?: T; error?: ApiError }>({ url, loading: true });
  useEffect(() => {
    let active = true;
    setState((current) => current.url === url ? { ...current, loading: true } : { url, loading: true });
    api<T>(url).then((data) => active && setState({ url, loading: false, data })).catch((error) => active && setState({ url, loading: false, error }));
    return () => { active = false; };
  }, [url, key, refresh]);
  const reload = useCallback(() => setRefresh((value) => value + 1), []);
  if (state.url !== url) return { loading: true, reload };
  return { loading: state.loading, data: state.data, error: state.error, reload };
}

export const errorMessage = (error: unknown, fallback = "Something went wrong. Please try again.") => (error as ApiError)?.error?.message || fallback;

/* ---------- Formatting ---------- */

export const formatNumber = (value: unknown) => value !== null && value !== "" && Number.isFinite(Number(value)) ? new Intl.NumberFormat().format(Number(value)) : "—";
export const formatDateTime = (value?: string | null) => { if (!value) return "—"; const parsed = Date.parse(value); return Number.isNaN(parsed) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed); };
export const formatDate = (value?: string | null) => { if (!value) return "unknown"; const parsed = Date.parse(value); return Number.isNaN(parsed) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(parsed); };
export const formatUptime = (seconds: number) => { const days = Math.floor(seconds / 86400); const hours = Math.floor(seconds % 86400 / 3600); return days ? `${days}d ${hours}h` : hours ? `${hours}h` : `${Math.max(0, Math.floor(seconds / 60))}m`; };
export const formatHours = (seconds: unknown) => { const value = Number(seconds) || 0; const hours = Math.floor(value / 3600); const minutes = Math.floor(value % 3600 / 60); return hours ? `${formatNumber(hours)}h ${minutes}m` : `${minutes}m`; };
// In-game money is shown in whole dollars, rounded down so a player never sees more than they have.
export const formatMoney = (value: unknown) => value !== null && value !== "" && Number.isFinite(Number(value)) ? "$" + formatNumber(Math.floor(Number(value))) : "—";
// Minecraft play time adds up to days quickly: "12d 4h", then "4h 12m", then "12m".
export const formatPlaytime = (seconds: unknown) => { const value = Math.max(0, Number(seconds) || 0); const days = Math.floor(value / 86400); const hours = Math.floor(value % 86400 / 3600); const minutes = Math.floor(value % 3600 / 60); return days ? `${formatNumber(days)}d ${hours}h` : hours ? `${hours}h ${minutes}m` : `${minutes}m`; };
export const humanize = (value: string) => value.replaceAll("-", " ").replaceAll("_", " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (letter) => letter.toUpperCase());
export const plural = (count: number, word: string) => `${formatNumber(count)} ${word}${count === 1 ? "" : "s"}`;

/* ---------- Status ---------- */

export type Tone = "success" | "warning" | "danger" | "info" | "neutral" | "accent";
const toneByStatus: Record<string, Tone> = {
  live: "success", healthy: "success", success: "success", sent: "success", complete: "success", published: "success", online: "success", ready: "success", enabled: "success", info: "info",
  cached: "warning", stale: "warning", degraded: "warning", warning: "warning", warn: "warning", partial: "warning", active: "warning", draft: "info",
  error: "danger", failed: "danger", failure: "danger", fatal: "danger", denied: "danger", blocked: "danger",
  debug: "neutral", disabled: "neutral", unavailable: "neutral", revoked: "neutral", offline: "neutral",
};
export const statusTone = (status?: string): Tone => toneByStatus[String(status || "").toLowerCase()] || "neutral";

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge tone-${tone}`}>{children}</span>;
}
export function StatusBadge({ status, children }: { status?: string; children?: ReactNode }) {
  return <Badge tone={statusTone(status)}><span className="badge-dot" aria-hidden="true"/>{children ?? humanize(String(status || "unknown"))}</Badge>;
}
export function StatusDot({ status, label }: { status?: string; label?: string }) {
  return <span className={`status-dot tone-${statusTone(status)}`} role="img" aria-label={label || humanize(String(status || "unknown"))}/>;
}

/* ---------- Layout ---------- */

export function PageHeader({ eyebrow, title, description, actions, meta }: { eyebrow?: string; title: ReactNode; description?: ReactNode; actions?: ReactNode; meta?: ReactNode }) {
  return <header className="page-header">
    <div className="page-header-copy">{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1>{title}</h1>{description && <p className="page-lede">{description}</p>}{meta && <div className="page-meta">{meta}</div>}</div>
    {actions && <div className="page-actions">{actions}</div>}
  </header>;
}

export function Panel({ title, eyebrow, description, action, children, className = "", as: Tag = "section", id }: { title?: ReactNode; eyebrow?: string; description?: ReactNode; action?: ReactNode; children?: ReactNode; className?: string; as?: "section" | "article" | "aside" | "div"; id?: string }) {
  const headingId = useId();
  return <Tag className={`panel ${className}`.trim()} aria-labelledby={title ? headingId : undefined} id={id}>
    {(title || action) && <div className="panel-head"><div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}{title && <h2 id={headingId}>{title}</h2>}{description && <p className="panel-description">{description}</p>}</div>{action && <div className="panel-action">{action}</div>}</div>}
    {children}
  </Tag>;
}

export function Metric({ label, value, icon, status, hint }: { label: string; value: ReactNode; icon?: IconName; status?: string; hint?: ReactNode }) {
  return <article className="metric">
    <div className="metric-top">{icon && <span className="metric-icon"><Icon name={icon} size={16}/></span>}<span className="metric-label">{label}</span>{status && <StatusDot status={status} label={`Source: ${status}`}/>}</div>
    <strong className="metric-value">{value ?? "—"}</strong>
    {hint && <span className="metric-hint">{hint}</span>}
  </article>;
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return <Link className="back-link" to={to}><Icon name="arrowLeft" size={16}/>{children}</Link>;
}

/* ---------- Tables ---------- */

export function Table({ headers, rows, empty, caption, numeric = [] }: { headers: string[]; rows: ReactNode[][]; empty: { title: string; description: string }; caption?: string; numeric?: number[] }) {
  if (!rows.length) return <EmptyState icon="search" title={empty.title} description={empty.description}/>;
  return <div className="table-wrap"><table>{caption && <caption className="sr-only">{caption}</caption>}
    <thead><tr>{headers.map((header, index) => <th key={header} scope="col" className={numeric.includes(index) ? "num" : undefined}>{header}</th>)}</tr></thead>
    <tbody>{rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex} data-label={headers[cellIndex]} className={numeric.includes(cellIndex) ? "num" : undefined}>{cell}</td>)}</tr>)}</tbody>
  </table></div>;
}

/* ---------- Form controls ---------- */

export function SearchField({ value, onChange, label, placeholder }: { value: string; onChange: (value: string) => void; label: string; placeholder?: string }) {
  return <label className="search-field"><span className="sr-only">{label}</span><Icon name="search" size={17}/><input type="search" value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder || label}/>{value && <button type="button" className="search-clear" aria-label="Clear search" onClick={() => onChange("")}><Icon name="close" size={14}/></button>}</label>;
}

export function Switch({ checked, onChange, label, description, disabled, hideLabel, ariaLabel }: { checked: boolean; onChange: (value: boolean) => void; label: string; description?: ReactNode; disabled?: boolean; hideLabel?: boolean; ariaLabel?: string }) {
  return <label className={`switch ${disabled ? "is-disabled" : ""}`}>
    <span className="switch-control"><input type="checkbox" checked={checked} disabled={disabled} aria-label={ariaLabel} onChange={(event) => onChange(event.target.checked)}/><span className="switch-track" aria-hidden="true"><span className="switch-thumb"/></span></span>
    <span className={hideLabel ? "sr-only" : "switch-copy"}><span className="switch-label">{label}</span>{description && <small>{description}</small>}</span>
  </label>;
}

/** Labels a single input/select/textarea child via htmlFor; hint and error text are linked with aria-describedby. */
export function Field({ label, hint, children, wide, error }: { label: ReactNode; hint?: ReactNode; children: ReactElement<{ id?: string; "aria-describedby"?: string; "aria-invalid"?: boolean }>; wide?: boolean; error?: string }) {
  const id = useId();
  const noteId = `${id}-note`;
  const note = error || hint;
  return <div className={`field ${wide ? "wide" : ""} ${error ? "has-error" : ""}`}>
    <label className="field-label" htmlFor={id}>{label}</label>
    {cloneElement(children, { id, "aria-describedby": note ? noteId : undefined, "aria-invalid": error ? true : undefined })}
    {note && <span id={noteId} className={error ? "field-error" : "field-hint"}>{note}</span>}
  </div>;
}

/* ---------- Feedback ---------- */

export function EmptyState({ icon = "spark", title, description, action, compact }: { icon?: IconName; title: string; description: ReactNode; action?: ReactNode; compact?: boolean }) {
  return <div className={`empty ${compact ? "compact" : ""}`}><span className="empty-icon"><Icon name={icon} size={22}/></span><h3>{title}</h3><p>{description}</p>{action}</div>;
}

export function Skeleton({ rows = 3, label = "Loading" }: { rows?: number; label?: string }) {
  return <div className="skeleton" role="status" aria-label={label}>{Array.from({ length: rows }, (_, index) => <span key={index}/>)}</div>;
}

export function ApiState({ state, unavailable }: { state: Pick<AsyncState<unknown>, "loading" | "error" | "reload">; unavailable?: string }) {
  if (state.loading) return <Skeleton/>;
  const code = state.error?.error?.code;
  return <div className="empty tone-error" role="alert"><span className="empty-icon"><Icon name="alert" size={22}/></span>
    <h3>{code ? humanize(code) : "Connection required"}</h3>
    <p>{state.error?.error?.message || unavailable || "This data source is unavailable."}</p>
    <div className="empty-actions"><button type="button" className="button" onClick={state.reload}><Icon name="refresh" size={16}/>Try again</button></div>
    {state.error?.error?.requestId && <small className="fine-print">Request {state.error.error.requestId}</small>}
  </div>;
}

export type NoticeValue = { tone: "success" | "error" | "info"; text: string } | null;
export function useNotice() {
  const [notice, setNotice] = useState<NoticeValue>(null);
  const show = useCallback((tone: "success" | "error" | "info", text: string) => setNotice({ tone, text }), []);
  return { notice, success: useCallback((text: string) => show("success", text), [show]), error: useCallback((text: string) => show("error", text), [show]), info: useCallback((text: string) => show("info", text), [show]), clear: useCallback(() => setNotice(null), []) };
}
export function Notice({ notice, onDismiss }: { notice: NoticeValue; onDismiss?: () => void }) {
  if (!notice) return null;
  const icon: IconName = notice.tone === "success" ? "check" : notice.tone === "error" ? "alert" : "info";
  return <div className={`notice tone-${notice.tone}`} role={notice.tone === "error" ? "alert" : "status"}><Icon name={icon} size={18}/><p>{notice.text}</p>{onDismiss && <button type="button" className="icon-button ghost" aria-label="Dismiss message" onClick={onDismiss}><Icon name="close" size={16}/></button>}</div>;
}

/* ---------- Confirm dialog ---------- */

type ConfirmOptions = { title: string; body?: ReactNode; confirmLabel: string; danger?: boolean; reason?: { label: string; minLength: number; placeholder?: string } };
type PendingConfirm = ConfirmOptions & { resolve: (value: string | null) => void };

/** Accessible replacement for window.confirm/prompt. Resolves to "" (or the typed reason) when confirmed, null when cancelled. */
export function useConfirm() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;
  useEffect(() => () => pendingRef.current?.resolve(null), []);
  const confirm = useCallback((options: ConfirmOptions) => new Promise<string | null>((resolve) => setPending({ ...options, resolve })), []);
  const close = (value: string | null) => { pending?.resolve(value); setPending(null); };
  return { confirm, dialog: pending ? <ConfirmDialog options={pending} onClose={close}/> : null };
}

/** While `dirty`, intercepts in-app link clicks and asks before leaving (beforeunload only covers reloads). */
export function useUnsavedChangesGuard(dirty: boolean, confirm: ReturnType<typeof useConfirm>["confirm"]) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!dirty) return;
    const guard = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.target === "_blank" || anchor.origin !== window.location.origin) return;
      const next = anchor.pathname + anchor.search + anchor.hash;
      if (next === window.location.pathname + window.location.search + window.location.hash) return;
      event.preventDefault();
      event.stopPropagation();
      void confirm({ title: "Leave without saving?", body: <p>Your unsaved changes on this page will be lost.</p>, confirmLabel: "Leave page", danger: true }).then((result) => { if (result !== null) navigate(next); });
    };
    document.addEventListener("click", guard, true);
    return () => document.removeEventListener("click", guard, true);
  }, [dirty, confirm, navigate]);
}

function ConfirmDialog({ options, onClose }: { options: ConfirmOptions; onClose: (value: string | null) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [reason, setReason] = useState("");
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const opener = document.activeElement as HTMLElement | null;
    if (typeof dialog.showModal === "function") dialog.showModal(); else dialog.setAttribute("open", "");
    return () => opener?.focus?.();
  }, []);
  const tooShort = !!options.reason && reason.trim().length < options.reason.minLength;
  return <dialog ref={ref} className="dialog" aria-labelledby={titleId} onCancel={(event) => { event.preventDefault(); onClose(null); }}>
    <form method="dialog" onSubmit={(event) => { event.preventDefault(); if (!tooShort) onClose(options.reason ? reason.trim() : ""); }}>
      <div className={`dialog-icon ${options.danger ? "tone-danger" : "tone-info"}`}><Icon name={options.danger ? "alert" : "info"} size={20}/></div>
      <h2 id={titleId}>{options.title}</h2>
      {options.body && <div className="dialog-body">{options.body}</div>}
      {options.reason && <Field label={options.reason.label} hint={`At least ${options.reason.minLength} characters. Recorded in the audit log.`}><textarea autoFocus value={reason} placeholder={options.reason.placeholder} onChange={(event) => setReason(event.target.value)} rows={3}/></Field>}
      <div className="dialog-actions"><button type="button" className="button" onClick={() => onClose(null)} autoFocus={!options.reason && options.danger}>Cancel</button><button type="submit" className={`button ${options.danger ? "danger" : "primary"}`} disabled={tooShort} autoFocus={!options.reason && !options.danger}>{options.confirmLabel}</button></div>
    </form>
  </dialog>;
}

/* ---------- Misc ---------- */

export function CopyButton({ value, label, className = "button primary", copiedLabel = "Copied" }: { value: string; label: string; className?: string; copiedLabel?: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => { if (state === "idle") return; const timer = setTimeout(() => setState("idle"), 2200); return () => clearTimeout(timer); }, [state]);
  const copy = () => { if (!navigator.clipboard) { setState("failed"); return; } navigator.clipboard.writeText(value).then(() => setState("copied"), () => setState("failed")); };
  return <button type="button" className={className} onClick={copy} aria-label={state === "idle" ? `${label}: ${value}` : undefined}>
    <Icon name={state === "copied" ? "check" : state === "failed" ? "alert" : "copy"} size={16}/>
    <span aria-live="polite">{state === "copied" ? copiedLabel : state === "failed" ? "Copy failed" : label}</span>
  </button>;
}

export function Avatar({ src, name, size = 40, square }: { src?: string | null; name: string; size?: number; square?: boolean }) {
  const [failed, setFailed] = useState(false);
  const style = { width: size, height: size, fontSize: Math.round(size * 0.42) };
  if (src && !failed) return <img className={`avatar ${square ? "square" : ""}`} src={src} alt="" width={size} height={size} style={style} onError={() => setFailed(true)}/>;
  return <span className={`avatar fallback ${square ? "square" : ""}`} style={style} aria-hidden="true">{(name || "?").trim().slice(0, 1).toUpperCase()}</span>;
}

export function FullState({ title, description, icon = "spark", action, loading }: { title: string; description: string; icon?: IconName; action?: ReactNode; loading?: boolean }) {
  return <main className={`full-state ${loading ? "is-loading" : ""}`} id="main">
    <span className="full-state-mark">{loading ? <span className="spinner" aria-hidden="true"/> : <Icon name={icon} size={26}/>}</span>
    <h1>{title}</h1><p>{description}</p>{action && <div className="full-state-actions">{action}</div>}
  </main>;
}
